import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, Eye, Edit, Trash, ChevronLeft, ChevronRight } from 'lucide-react';
import axiosInstance from '../../../../axiosConfig';

export default function TicketManagement() {
  const navigate = useNavigate();
  const [metaData, setMetaData] = useState({});
  const [destinationList, setDestinationList] = useState([]);
  const [merchantList, setMerchantList] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [tickets, setTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [merchantLoading, setMerchantLoading] = useState(false);

  const statusOptions = ['Active', 'Inactive', 'Draft'];

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-emerald-50 text-emerald-700';
      case 'Inactive': return 'bg-slate-100 text-slate-600';
      case 'Draft': return 'bg-amber-50 text-amber-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  // Fetch tickets from API
  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        'internal-api/v1/client-web/destination-panel/ticket/available-tickets',
        {
          destinationIds: [],
          ticketOriginsIds: [],
          ticketAgerIds: [],
          ticketTypeIds: [],
          isGroupBtGroupCode : true
        }
      );
      
      const nextTickets = Array.isArray(response.data?.result) ? response.data.result : [];
      setTickets(nextTickets);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      // Fallback to empty array in case of error
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch destination list
  const fetchDestinationList = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        '/internal-api/v1/client-web/destination-panel/config/list',
        {
          params: {
            page: currentPage,
            size: itemsPerPage,
          }
        }
      );
      if (response.data?.data) {
        setDestinationList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching destinations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch merchant list
  const fetchMerchantList = async () => {
    try {
      setMerchantLoading(true);
      const response = await axiosInstance.get('/internal-api/v1/client-web/entity-panel/config/list');
      
      if (response.data?.data) {
        setMerchantList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching merchants:", error);
      // Fallback merchant data in case of API error
      setMerchantList([
        { id: 1, name: 'Cultural Triangle Authority' },
        { id: 2, name: 'Wildlife Conservation Dept' },
        { id: 3, name: 'Central Cultural Department' }
      ]);
    } finally {
      setMerchantLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchDestinationList();
    fetchMerchantList();
  }, []);

  // Filter tickets based on search and filter criteria
  const filteredTickets = tickets.filter(ticket => {
    const safeName = String(ticket.name || '');
    const safeId = String(ticket.id || '');
    const safeType = String(ticket.typeName || '');
    const safeGroup = String(ticket.ticketGroupCode || '');
    const matchesSearch = safeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         safeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         safeType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         safeGroup.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDestination = !selectedDestination || ticket.destination === selectedDestination;
    const matchesMerchant = !selectedMerchant || ticket.merchant === selectedMerchant;
    const matchesStatus = !selectedStatus || ticket.status === selectedStatus;
    
    return matchesSearch && matchesDestination && matchesMerchant && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / itemsPerPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const startIndex = (currentPageSafe - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const pagedTickets = filteredTickets.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleFilter = () => {
    console.log('Applying filters:', {
      searchTerm,
      selectedDestination,
      selectedMerchant,
      selectedStatus
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedDestination('');
    setSelectedMerchant('');
    setSelectedStatus('');
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, searchTerm, selectedDestination, selectedMerchant, selectedStatus]);

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Booking Types</h1>
          <p className="mt-0.5 text-xs text-slate-500">Home / Booking Types / Tickets</p>
        </div>
        <a
          href="/TicketTabs"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark transition-colors"
        >
          + Add New Ticket
        </a>
      </div>

      {/* Tickets | Rooms Tabs */}
      <div className="inline-flex p-1 bg-white border rounded-lg border-slate-200">
        <button
          className="px-4 py-1.5 rounded-md text-sm font-semibold bg-brand-blue text-white"
        >
          Tickets
        </button>
        <button
          onClick={() => navigate('/DestinationList?type=Hotels')}
          className="px-4 py-1.5 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Rooms
        </button>
      </div>

      <div className="bg-white border rounded-xl border-slate-200">
        <div className="p-5">
          {/* Search and Filter Section */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 flex items-center pointer-events-none left-3">
                <Search className="w-4 h-4 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2.5 pl-10 pr-4 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              />
            </div>

            {/* Destination Filter */}
            <div className="flex-shrink-0">
              <div className="relative">
                <select
                  value={selectedDestination}
                  onChange={(e) => setSelectedDestination(e.target.value)}
                  className="py-2.5 pl-3 pr-8 text-sm bg-white border rounded-lg appearance-none border-slate-300 max-w-48 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  disabled={loading}
                >
                  <option value="">All Destinations</option>
                  {destinationList.map((destination) => (
                    <option key={destination.id} value={destination.name}>
                      {destination.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            </div>

            {/* Merchant Filter */}
            <div className="flex-shrink-0">
              <div className="relative">
                <select
                  value={selectedMerchant}
                  onChange={(e) => setSelectedMerchant(e.target.value)}
                  className="py-2.5 pl-3 pr-8 text-sm bg-white border rounded-lg appearance-none border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  disabled={merchantLoading}
                >
                  <option value="">All Merchants</option>
                  {merchantList.map((merchant) => (
                    <option key={merchant.id} value={merchant.name}>
                      {merchant.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </div>
                {merchantLoading && (
                  <div className="absolute inset-y-0 flex items-center right-6">
                    <div className="w-3 h-3 border-b-2 rounded-full animate-spin border-brand-blue"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex-shrink-0">
              <div className="relative">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="py-2.5 pl-3 pr-8 text-sm bg-white border rounded-lg appearance-none border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                >
                  <option value="">All Status</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            </div>

            {/* Button Group: Clear + Filter */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2.5 text-sm font-semibold border rounded-lg text-slate-600 border-slate-300 hover:bg-slate-50 transition-colors"
              >
                Clear
              </button>

              <button
                onClick={handleFilter}
                className="px-4 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg bg-brand-blue hover:bg-brand-blue-dark"
              >
                Filter
              </button>
            </div>
          </div>

          {/* Results Summary */}
          {(searchTerm || selectedDestination || selectedMerchant || selectedStatus) && (
            <div className="p-3 mb-4 border rounded-lg border-brand-blue/20 bg-brand-blue/5">
              <div className="text-xs text-brand-blue">
                Showing {pagedTickets.length} of {filteredTickets.length} tickets
                {searchTerm && <span className="ml-2">&middot; Search: "{searchTerm}"</span>}
                {selectedDestination && <span className="ml-2">&middot; Destination: {selectedDestination}</span>}
                {selectedMerchant && <span className="ml-2">&middot; Merchant: {selectedMerchant}</span>}
                {selectedStatus && <span className="ml-2">&middot; Status: {selectedStatus}</span>}
              </div>
            </div>
          )}

          {/* Table Section */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-slate-50 border-slate-200">
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Ticket ID</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Ticket Name</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Ticket Type</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Destination</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Group Code</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="py-10 text-sm text-center text-slate-500">
                      Loading tickets...
                    </td>
                  </tr>
                ) : pagedTickets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-10 text-sm text-center text-slate-400">
                      No tickets found matching your criteria
                    </td>
                  </tr>
                ) : (
                  pagedTickets.map(ticket => (
                    <tr key={ticket.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-semibold text-brand-blue">{ticket.id}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{ticket.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{ticket.typeName || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{ticket.destinationName || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{ticket.ticketGroupCode || '-'}</td>
                      <td className="px-4 py-3">
                        {ticket.status ? (
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusBgColor(ticket.status)}`}>
                            {ticket.status}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button className="p-1.5 transition-colors rounded-full bg-slate-100 hover:bg-slate-200">
                            <Eye className="w-4 h-4 text-slate-600" />
                          </button>
                          <button className="p-1.5 transition-colors rounded-full bg-slate-100 hover:bg-slate-200">
                            <Edit className="w-4 h-4 text-slate-600" />
                          </button>
                          <button className="p-1.5 transition-colors rounded-full bg-slate-100 hover:bg-slate-200">
                            <Trash className="w-4 h-4 text-slate-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Section */}
          <div className="flex items-center justify-between pt-4 mt-4 text-xs border-t border-slate-200 text-slate-500">
            <div>
              Showing {filteredTickets.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredTickets.length)} of {filteredTickets.length} tickets
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPageSafe === 1}
                className="px-3 py-1 border rounded-lg border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 border rounded-lg border-slate-300 bg-slate-50">
                Page {currentPageSafe} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPageSafe === totalPages}
                className="px-3 py-1 border rounded-lg border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="flex items-center ml-4">
                <span className="mr-2">Show</span>
                <div className="relative">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="py-1 pl-2 pr-6 bg-white border rounded-lg appearance-none border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-1 pointer-events-none">
                    <ChevronDown className="w-3 h-3 text-slate-400" />
                  </div>
                </div>
                <span className="ml-2">per page</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
